import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import UserLayout from './UserLayout';
import WelcomeCard from './components/WelcomeCard';
import QuickActions from './components/QuickActions';
import SavedPropertiesPreview from './components/SavedPropertiesPreview';
import MyPropertyRequests from './components/MyPropertyRequests';
import RecentSearches from './components/RecentSearches';
import SearchAlerts from './components/SearchAlerts';
import RecentlyViewed from './components/RecentlyViewed';
import ScheduledTours from './components/ScheduledTours';
import ActivityFeed from './components/ActivityFeed';
import Icon from '../../components/AppIcon';
import {
  selectCurrentUser,
  selectIsAuthenticated,
} from '../../features/auth/authSlice';
import {
  useGetUserDataQuery,
  useGetUserFavouritePropertyQuery,
  useGetUserSavedPropertiesQuery,
  useGetUserSearchesQuery,
  useGetUserAlertsQuery,
  useGetUserToursQuery,
  useGetUserActivitiesQuery,
  useGetUserRecentlyViewedQuery,
  useGetMyPropertyNeedsQuery,
  useGetWorkspaceBookingsQuery,
  useDeleteFavoriteMutation,
} from '../../features/api/apiSlice';
import { normalizeProperty } from './utils';
import { useToast } from '../../components/ui/Toast';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value?.data && Array.isArray(value.data)) return value.data;
  return [];
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authUser = useSelector(selectCurrentUser);
  const addToast = useToast();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/user-dashboard' } } });
    }
  }, [isAuthenticated, navigate]);

  const { data: userData, isLoading: userLoading } = useGetUserDataQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Primary favourites endpoint (known production API)
  const {
    data: favouritesRaw,
    isLoading: favLoading,
    isError: favError,
    refetch: refetchFavourites,
  } = useGetUserFavouritePropertyQuery(undefined, { skip: !isAuthenticated });

  // Optional legacy saved-properties table
  const { data: savedRaw, isLoading: savedLoading } = useGetUserSavedPropertiesQuery(undefined, {
    skip: !isAuthenticated,
  });

  const { data: searchesRaw, isLoading: searchesLoading } = useGetUserSearchesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: alertsRaw, isLoading: alertsLoading } = useGetUserAlertsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: toursRaw, isLoading: toursLoading } = useGetUserToursQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: activitiesRaw, isLoading: activitiesLoading } = useGetUserActivitiesQuery(
    undefined,
    { skip: !isAuthenticated }
  );
  const { data: viewedRaw, isLoading: viewedLoading } = useGetUserRecentlyViewedQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: myNeedsRaw, isLoading: needsLoading } = useGetMyPropertyNeedsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: bookingsRaw, isLoading: bookingsLoading } = useGetWorkspaceBookingsQuery(
    { as: 'client' },
    { skip: !isAuthenticated }
  );

  const [deleteFavorite] = useDeleteFavoriteMutation();

  const user = useMemo(() => {
    if (userData?.data) return { ...authUser, ...userData.data };
    if (userData && typeof userData === 'object' && !Array.isArray(userData)) {
      return { ...authUser, ...userData };
    }
    return authUser;
  }, [userData, authUser]);

  const savedProperties = useMemo(() => {
    const fromFavourites = asArray(favouritesRaw)
      .map(normalizeProperty)
      .filter(Boolean);
    if (fromFavourites.length) return fromFavourites;

    const fromSaved = asArray(savedRaw)
      .map((item) => normalizeProperty(item.property ? item : { property: item.property || item, ...item }))
      .filter(Boolean);

    // de-dupe by id
    const map = new Map();
    fromSaved.forEach((p) => map.set(String(p.id), p));
    return Array.from(map.values());
  }, [favouritesRaw, savedRaw]);

  const recentSearches = asArray(searchesRaw);
  const searchAlerts = asArray(alertsRaw);
  const scheduledTours = asArray(toursRaw);
  const activities = asArray(activitiesRaw);
  const myNeeds = asArray(myNeedsRaw);
  const bookings = asArray(bookingsRaw);

  const recentlyViewed = useMemo(
    () =>
      asArray(viewedRaw)
        .map(normalizeProperty)
        .filter(Boolean),
    [viewedRaw]
  );

  const upcomingBookings = useMemo(() => {
    return bookings
      .filter((b) => {
        const st = String(b.status || '').toLowerCase();
        return !['cancelled', 'completed', 'no_show'].includes(st);
      })
      .slice(0, 8);
  }, [bookings]);

  // Synthetic activity when backend activity feed is empty
  const fallbackActivity = useMemo(() => {
    const items = [];
    myNeeds.slice(0, 3).forEach((n) => {
      items.push({
        id: `need-${n.id}`,
        type: 'request',
        action: 'Posted property request',
        propertyTitle: n.title,
        timestamp: n.created_at,
      });
    });
    savedProperties.slice(0, 3).forEach((p) => {
      items.push({
        id: `save-${p.id}`,
        type: 'save',
        action: 'Saved property',
        propertyTitle: p.title,
        timestamp: p.savedDate || null,
      });
    });
    recentlyViewed.slice(0, 3).forEach((p) => {
      items.push({
        id: `view-${p.id}`,
        type: 'view',
        action: 'Viewed property',
        propertyTitle: p.title,
        timestamp: p.viewedDate,
      });
    });
    return items;
  }, [myNeeds, savedProperties, recentlyViewed]);

  const stats = {
    saved: savedProperties.length,
    requests: myNeeds.length,
    bookings: upcomingBookings.length || scheduledTours.length,
  };

  const handleRemoveFavorite = async (propertyId) => {
    try {
      await deleteFavorite(propertyId).unwrap();
      addToast('Removed from saved properties.', 'info');
      refetchFavourites();
    } catch (err) {
      addToast(err?.data?.message || 'Could not remove property.', 'error');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const pageLoading = userLoading && !user;

  return (
    <UserLayout
      title={pageLoading ? 'Loading…' : `Welcome back`}
      subtitle="Your personal property command center"
    >
      <div className="space-y-6 max-w-7xl">
        <WelcomeCard user={user} stats={stats} />

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Kpi
            label="Saved properties"
            value={stats.saved}
            icon="Heart"
            hint="Shortlisted homes"
            loading={favLoading || savedLoading}
          />
          <Kpi
            label="Property requests"
            value={stats.requests}
            icon="ClipboardList"
            hint="On the notice board"
            loading={needsLoading}
          />
          <Kpi
            label="Upcoming bookings"
            value={stats.bookings}
            icon="Calendar"
            hint="Advisory & tours"
            loading={bookingsLoading || toursLoading}
          />
          <Kpi
            label="Recently viewed"
            value={recentlyViewed.length}
            icon="Eye"
            hint="Continue browsing"
            loading={viewedLoading}
          />
        </div>

        {favError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
            <Icon name="AlertTriangle" size={18} className="mt-0.5 flex-shrink-0" />
            <p>
              We could not refresh your saved properties right now. You can still browse the market
              and try again later.
            </p>
          </div>
        )}

        <QuickActions />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <SavedPropertiesPreview
              properties={savedProperties}
              isLoading={favLoading || savedLoading}
              onRemove={handleRemoveFavorite}
            />
            <MyPropertyRequests requests={myNeeds} isLoading={needsLoading} />
            <RecentlyViewed properties={recentlyViewed} isLoading={viewedLoading} />
            <RecentSearches recentSearches={recentSearches} isLoading={searchesLoading} />
          </div>

          <div className="space-y-6">
            <ScheduledTours
              bookings={upcomingBookings}
              tours={scheduledTours}
              isLoading={bookingsLoading || toursLoading}
            />
            <ActivityFeed
              activities={activities}
              isLoading={activitiesLoading}
              fallbackItems={fallbackActivity}
            />
            <SearchAlerts searchAlerts={searchAlerts} isLoading={alertsLoading} />

            <section className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-5 shadow-sm">
              <p className="text-[10px] uppercase tracking-[0.18em] text-sky-400 font-semibold mb-2">
                Tips for property seekers
              </p>
              <ul className="text-sm text-slate-300 space-y-2.5 leading-relaxed">
                <li className="flex gap-2">
                  <Icon name="CheckCircle2" size={16} className="text-sky-400 mt-0.5 flex-shrink-0" />
                  Save properties you like so you can compare later.
                </li>
                <li className="flex gap-2">
                  <Icon name="CheckCircle2" size={16} className="text-sky-400 mt-0.5 flex-shrink-0" />
                  Post a Specific Property Request for hard-to-find homes.
                </li>
                <li className="flex gap-2">
                  <Icon name="CheckCircle2" size={16} className="text-sky-400 mt-0.5 flex-shrink-0" />
                  Book verified agents for guided viewings and advice.
                </li>
              </ul>
              <Link
                to="/notice-board"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-300 hover:text-sky-200"
              >
                Open notice board
                <Icon name="ArrowRight" size={14} />
              </Link>
            </section>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

const Kpi = ({ label, value, icon, hint, loading }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center">
        <Icon name={icon} size={16} />
      </span>
    </div>
    <p className="text-2xl font-bold text-slate-900 tabular-nums">
      {loading ? '…' : value}
    </p>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default UserDashboard;
